import useApiService from '~/composables/api/useApiService';
import { apiEndpoints } from '@/constants';
import type {
  CaptureTransactionBodyType,
  ChargebackBodyType,
  CheckRequestBodyType
} from '~/types/transactions/methods';
import type {
  ApiError,
  BankAccountInfosType,
  SimulateMoneyInApiBodyType,
  TransactionActionBodyType,
  TransactionDetailsType,
  TransactionInfosP2PType,
  TransactionInfosType,
  TransactionResponseType,
  TransactionHistoryStep
} from '~/types';

export default function useTransactionsApi() {
  const { post, put, get } = useApiService();

  const checkRequest = async (body: CheckRequestBodyType, loadingFlag: string) => {
    const { error } = await post(`${apiEndpoints.transactions}/out/validate`, body, loadingFlag);

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }
  };

  const getInOutTransactionDetails = async (transactionId: string) => {
    const { data, error } = await get(
      `${apiEndpoints.transactions}/inOut/${transactionId}`,
      'getInOutTransaction'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as TransactionDetailsType;
  };

  const getP2PTransactionDetails = async (transactionId: string) => {
    const { data, error } = await get(
      `${apiEndpoints.transactions}/P2p/${transactionId}`,
      'getP2PTransaction'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as TransactionInfosP2PType;
  };

  const getInOutRelatedTransactions = async (transactionId: string, queryParams: string) => {
    const { data, error } = await get(
      `${apiEndpoints.transactions}/inOut/${transactionId}/RelatedTransactions?${queryParams}`,
      'getInOutRelatedTransactions'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as TransactionResponseType[];
  };

  const getTransactionsData = async (loadUrl: string, params: string) => {
    const { data, error } = await get(`${loadUrl}?${params}`);

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as { data: TransactionInfosType[]; groupCount: number; totalCount: number };
  };

  const getP2PRelatedTransactions = async (transactionId: string) => {
    const { data, error } = await get(
      `${apiEndpoints.transactions}/P2P/${transactionId}/RelatedTransactions`,
      'getP2PRelatedTransactions'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(JSON.stringify(cause));
    }

    return data.value as TransactionInfosType;
  };

  const getTransactionRibInfos = async (rib: string) => {
    const { data, error } = await get(`${apiEndpoints.ibans}/${rib}`, 'getTransactionRibInfos');

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as BankAccountInfosType;
  };

  const postSendPaymentP2P = async (body: { [key: string]: any }) => {
    const { data, error } = await post(`${apiEndpoints.transactions}/p2p`, body, 'sendPayment');

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as { Id: number };
  };

  const postRefundPayment = async (body: { [key: string]: any }) => {
    const { data, error } = await post(
      `${apiEndpoints.transactions}/refund`,
      body,
      'refundPayment'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as { Id: number };
  };

  const postSimulateMoneyIn = async (body: SimulateMoneyInApiBodyType) => {
    const url = body.TransferId
      ? `${apiEndpoints.transactions}/in/check`
      : `${apiEndpoints.transactions}/in/transfer`;
    const { data, error } = await post(url, body, 'simulateMoneyIn');

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as { Id: number };
  };

  const postSimulateChargeBack = async (body: ChargebackBodyType) => {
    const { error } = await post('/chargebacks', body, 'chargeBack');

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }
  };

  const putConfirmTransaction = async (url: string, body: TransactionActionBodyType) => {
    const { error } = await put(
      `${apiEndpoints.transactions}${url}`,
      body,
      'processingTransactionAction'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }
  };

  const putCaptureTransaction = async (transactionId: string, body: CaptureTransactionBodyType) => {
    const { error } = await put(
      `${apiEndpoints.transactions}/InOut/${transactionId}/validate`,
      body,
      'captureTransaction'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }
  };

  const getTransactionHistory = async (transactionId: string) => {
    const { error, data } = await get(
      `${apiEndpoints.transactionsHistory}/${transactionId}`,
      'getTransactionHistory'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }
    return data.value as TransactionHistoryStep[];
  };

  return {
    checkRequest,
    getInOutTransactionDetails,
    getP2PTransactionDetails,
    getP2PRelatedTransactions,
    getInOutRelatedTransactions,
    getTransactionsData,
    getTransactionRibInfos,
    postSendPaymentP2P,
    postRefundPayment,
    postSimulateMoneyIn,
    postSimulateChargeBack,
    putConfirmTransaction,
    putCaptureTransaction,
    getTransactionHistory
  };
}
